import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { 
  TrendingUp, 
  CheckCircle, 
  Clock, 
  AlertCircle, 
  ExternalLink,
  RefreshCw,
  Loader2,
  Tag,
  BarChart3
} from 'lucide-react';
import { apiFetch } from '@/hooks/useFetch';

const GoogleAdsDashboard = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [campaignData, setCampaignData] = useState(null);
  const [conversionStatus, setConversionStatus] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [configRes, campaignsRes, conversionRes] = await Promise.all([
        apiFetch('/api/analytics/google/config'),
        apiFetch('/api/analytics/google/campaigns'),
        apiFetch('/api/analytics/google/conversion-status')
      ]);

      if (!configRes.error) setConfig(configRes.data);
      if (!campaignsRes.error) setCampaignData(campaignsRes.data);
      if (!conversionRes.error) setConversionStatus(conversionRes.data);
    } catch (error) {
      toast.error('Failed to load Google Ads data');
    } finally {
      setIsLoading(false);
    }
  };

  const formatCustomerId = (id) => {
    if (!id) return 'Not set';
    const str = id.toString();
    return `${str.slice(0,3)}-${str.slice(3,6)}-${str.slice(6)}`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-3">Loading Google Ads data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <svg className="w-7 h-7" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Google Ads Dashboard
          </h2>
          <p className="text-muted-foreground text-sm mt-1">
            Customer ID: {formatCustomerId(config?.customer_id)}
          </p>
        </div>
        <Button variant="outline" size="icon" onClick={fetchData}>
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      {/* API Status */}
      <Card className="bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border-yellow-500/30">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-full bg-yellow-500/20">
              <Clock className="h-6 w-6 text-yellow-500" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-lg">API Access: Test Mode</h3>
              <p className="text-muted-foreground mt-1">
                Your Developer Token is configured, but you need <strong>Basic Access</strong> approval 
                to view production campaign data.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <a 
                  href="https://ads.google.com/home/tools/manager-accounts/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-primary hover:underline text-sm"
                >
                  Create Manager Account <ExternalLink className="h-3 w-3" />
                </a>
                <span className="text-muted-foreground">•</span>
                <a 
                  href="https://developers.google.com/google-ads/api/docs/get-started/dev-token"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-primary hover:underline text-sm"
                >
                  Apply for Basic Access <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tracking Status - This is working! */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Tag className="h-5 w-5 text-green-500" />
            Tracking Status
            <span className="ml-2 px-2 py-1 bg-green-500/20 text-green-400 rounded-full text-xs">Active</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            {/* GTM */}
            <div className="p-4 rounded-lg bg-muted/50 border border-border">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="h-5 w-5 text-green-500" />
                <span className="font-medium">Google Tag Manager</span>
              </div>
              <code className="text-sm text-primary bg-primary/10 px-2 py-1 rounded">
                {conversionStatus?.tracking_codes?.google_tag_manager?.id || 'GTM-5RRMXCLL'}
              </code>
              <p className="text-xs text-muted-foreground mt-2">Installed in &lt;head&gt; and &lt;body&gt;</p>
            </div>

            {/* GA4 */}
            <div className="p-4 rounded-lg bg-muted/50 border border-border">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="h-5 w-5 text-green-500" />
                <span className="font-medium">Google Analytics 4</span>
              </div>
              <code className="text-sm text-primary bg-primary/10 px-2 py-1 rounded">
                {conversionStatus?.tracking_codes?.google_analytics?.id || 'G-3NHBZTMH4W'}
              </code>
              <p className="text-xs text-muted-foreground mt-2">Tracking page views & events</p>
            </div>

            {/* Conversion Tracking */}
            <div className="p-4 rounded-lg bg-muted/50 border border-border">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="h-5 w-5 text-green-500" />
                <span className="font-medium">Conversion Tracking</span>
              </div>
              <code className="text-sm text-primary bg-primary/10 px-2 py-1 rounded">
                {config?.conversion_id || 'AW-17989036900'}
              </code>
              <p className="text-xs text-muted-foreground mt-2">Ready for conversion actions</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Configuration */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Configuration
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex justify-between items-center py-2 border-b border-border">
              <span className="text-muted-foreground">Customer ID</span>
              <span className="font-mono">{formatCustomerId(config?.customer_id)}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-border">
              <span className="text-muted-foreground">Developer Token</span>
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm">••••••••{config?.has_developer_token ? 'VpfJrQ' : 'Not set'}</span>
                <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded text-xs">Test</span>
              </div>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-border">
              <span className="text-muted-foreground">Conversion ID</span>
              <span className="font-mono">{config?.conversion_id || 'Not set'}</span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-muted-foreground">Access Level</span>
              <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded text-xs uppercase">
                {config?.access_level || 'Test'}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Next Steps */}
      <Card className="bg-muted/30 border-dashed">
        <CardContent className="p-6">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-blue-500" />
            Next Steps to Enable Campaign Data
          </h3>
          <ol className="list-decimal list-inside space-y-2 text-muted-foreground">
            <li>Create a <strong>Google Ads Manager Account</strong> (if not done)</li>
            <li>Link your advertiser account (761-004-2107) to the manager account</li>
            <li>Apply for <strong>Basic Access</strong> in the API Center</li>
            <li>Once approved, campaign data will automatically appear here</li>
          </ol>
          <p className="mt-4 text-sm text-muted-foreground">
            <strong>Note:</strong> Basic Access approval typically takes 1-3 business days.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default GoogleAdsDashboard;
